import React, { useState, useEffect } from 'react';
import { useDirectionStats } from '../hooks/useDirectionStats';
import FiscalYearSelector from '../components/FiscalYearSelector';
import { formatCurrency } from '../utils/format';
import { ChevronDown, ChevronRight, Medal, ArrowUp, ArrowDown, Eye, Calculator, Loader2 } from 'lucide-react';

interface AccountingObjectivesProps {}

export default function AccountingObjectives({}: AccountingObjectivesProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [fiscalYear, setFiscalYear] = useState<number>(() => {
    const today = new Date();
    const month = today.getMonth() + 1;
    return month <= 4 ? today.getFullYear() - 1 : today.getFullYear();
  });
  const [period, setPeriod] = useState<'year' | 'quarter' | 'month'>('year');
  const [selectedPeriod, setSelectedPeriod] = useState('1');

  const { directors, loading, error, loadData } = useDirectionStats();

  useEffect(() => {
    const { startDate, endDate } = getDateRange();
    loadData(startDate, endDate);
  }, [fiscalYear, period, selectedPeriod]);

  const getDateRange = () => {
    let startDate: string;
    let endDate: string;

    if (period === 'year') {
      startDate = `${fiscalYear}-04-01`;
      endDate = `${fiscalYear + 1}-03-31`;
    } else if (period === 'quarter') {
      const quarterMap = {
        '1': { start: '04', end: '06' },
        '2': { start: '07', end: '09' },
        '3': { start: '10', end: '12' },
        '4': { start: '01', end: '03' }
      };
      const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap];
      const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
      startDate = `${year}-${quarter.start}-01`;
      endDate = `${year}-${quarter.end}-${new Date(year, parseInt(quarter.end), 0).getDate()}`;
    } else {
      const month = selectedPeriod.padStart(2, '0');
      const year = parseInt(selectedPeriod) <= 3 ? fiscalYear + 1 : fiscalYear;
      startDate = `${year}-${month}-01`;
      endDate = `${year}-${month}-${new Date(year, parseInt(month), 0).getDate()}`;
    }

    return { startDate, endDate };
  };

  const handlePeriodChange = (year: number, newPeriod: 'year' | 'quarter' | 'month', newSelectedPeriod: string) => {
    setFiscalYear(year);
    
    // Si on change de type de période, ajuster selectedPeriod
    if (newPeriod !== period) {
      if (newPeriod === 'month') {
        // Passer au mois actuel ou avril par défaut
        const today = new Date();
        const currentMonth = today.getMonth() + 1;
        const defaultMonth = currentMonth >= 4 ? String(currentMonth).padStart(2, '0') : '04';
        setPeriod(newPeriod);
        setSelectedPeriod(defaultMonth);
      } else if (newPeriod === 'quarter') {
        setPeriod(newPeriod);
        setSelectedPeriod('1');
      } else {
        setPeriod(newPeriod);
        setSelectedPeriod('1');
      }
    } else {
      // Si on reste sur le même type de période, juste mettre à jour la valeur
      setPeriod(newPeriod);
      setSelectedPeriod(newSelectedPeriod);
    }
  };

  const toggleItem = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const getCompletionColor = (rate: number) => {
    if (rate >= 100) return 'text-green-600';
    if (rate >= 75) return 'text-blue-600';
    if (rate >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCompanyColor = (company: string) => {
    switch (company) {
      case 'KI':
        return 'bg-blue-50 text-blue-800';
      case 'KA':
        return 'bg-emerald-50 text-emerald-800';
      case 'DRYADE':
        return 'bg-violet-50 text-violet-800';
      default:
        return 'bg-gray-50 text-gray-800';
    }
  };

  const getRowBackground = (level: number) => {
    switch (level) {
      case 0:
        return 'bg-slate-50/80 font-medium';
      case 1:
        return 'bg-indigo-50/20';
      default:
        return 'bg-white';
    }
  };

  const calculateObjective = (annualObjective: number) => {
    switch (period) {
      case 'year':
        return annualObjective;
      case 'quarter':
        return Math.round(annualObjective / 4);
      case 'month':
        return Math.round(annualObjective / 12);
      default:
        return annualObjective;
    }
  };

  const renderRow = (
    label: string,
    code: string,
    objective: number,
    accountingObjective: number,
    totalSales: number,
    potentialSales: number,
    transactionCount: number,
    level: number = 0,
    hasChildren: boolean = false,
    isExpanded: boolean = false,
    directorId: string = '',
    agencyName: string = '',
    company: string = ''
  ) => {
    const paddingLeft = level * 20;
    const periodObjective = calculateObjective(objective);
    const completion_rate = periodObjective ? (totalSales / periodObjective) * 100 : 0;
    const remaining = periodObjective - totalSales;

    return (
      <tr key={`${directorId}-${agencyName}-${code}`} className={`border-b border-slate-100 last:border-0 transition-colors ${getRowBackground(level)}`}>
        <td className="px-5 py-3 whitespace-nowrap">
          <div className="flex items-center" style={{ paddingLeft: `${paddingLeft}px` }}>
            {hasChildren ? (
              <button
                onClick={() => toggleItem(code)}
                className="mr-2 focus:outline-none"
              >
                {isExpanded ? (
                  <ChevronDown size={15} className="text-slate-400" />
                ) : (
                  <ChevronRight size={15} className="text-slate-400" />
                )}
              </button>
            ) : (
              <div className="w-6" />
            )}
            <div className="flex items-center space-x-2">
              <span className="font-medium">{label}</span>
              {level === 0 && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getCompanyColor(company)}`}>
                  {company}
                </span>
              )}
            </div>
          </div>
        </td>
        <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
          {formatCurrency(periodObjective)}
        </td>
        <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
          {formatCurrency(accountingObjective)}
        </td>
        <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
          {formatCurrency(potentialSales)}
        </td>
        <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-green-600">
          {formatCurrency(totalSales)}
        </td>
        <td className="px-5 py-3 whitespace-nowrap">
          <div className="flex items-center space-x-2">
            <span className={`text-sm font-medium ${remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(Math.abs(remaining))}
            </span>
            {remaining > 0 ? (
              <span className="text-xs text-red-500">↑</span>
            ) : (
              <span className="text-xs text-green-500">↓</span>
            )}
          </div>
        </td>
        <td className="px-5 py-3 whitespace-nowrap">
          <span className={`text-sm font-medium ${getCompletionColor(completion_rate)}`}>
            {completion_rate.toFixed(1)}%
          </span>
        </td>
        <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
          {transactionCount}
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <p className="text-sm text-slate-500">Chargement des objectifs…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">{error}</div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-7 py-6 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-violet-500/10 rounded-full translate-y-1/2 pointer-events-none" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <Calculator size={22} className="text-indigo-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Objectifs Comptables</h1>
              <p className="text-sm text-slate-400 mt-0.5">Suivi hiérarchique des objectifs par direction</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <FiscalYearSelector
              value={fiscalYear}
              period={period}
              selectedPeriod={selectedPeriod}
              onChange={handlePeriodChange}
              variant="dark"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Structure
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Objectif {period === 'year' ? 'Annuel' : period === 'quarter' ? 'Trimestriel' : 'Mensuel'}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Obj. Compta
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  HONO Potentiel
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  HONO Net HT
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Reste à réaliser
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  % Réalisation
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Nb Trans.
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {directors.map(director => (
                <React.Fragment key={director.id}>
                  {renderRow(
                    director.name,
                    director.code,
                    director.annual_objective,
                    director.accounting_objective,
                    director.total_sales,
                    director.potential_sales,
                    director.sales_count,
                    0,
                    director.agencies.length > 0,
                    expandedItems.has(director.code),
                    director.id,
                    '',
                    director.company
                  )}
                  {expandedItems.has(director.code) && director.agencies.map(agency => (
                    <React.Fragment key={`${director.id}-${agency.name}`}>
                      {renderRow(
                        agency.name,
                        agency.code,
                        agency.annual_objective,
                        agency.accounting_objective,
                        agency.total_sales,
                        agency.potential_sales,
                        agency.sales_count,
                        1,
                        agency.negotiators.length > 0,
                        expandedItems.has(agency.code),
                        director.id,
                        agency.name
                      )}
                      {expandedItems.has(agency.code) && agency.negotiators.map(negotiator => (
                        <tr
                          key={`${director.id}-${agency.name}-${negotiator.id}`}
                          className="border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors"
                        >
                          <td className="px-5 py-3 whitespace-nowrap">
                            <div className="flex items-center" style={{ paddingLeft: '40px' }}>
                              <div className="w-6" />
                              <span className="font-normal">{negotiator.name}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(calculateObjective(negotiator.annual_objective))}
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            -
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                            {formatCurrency(negotiator.potential_sales)}
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-green-600">
                            {formatCurrency(negotiator.total_sales)}
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className={`text-sm font-medium ${
                                calculateObjective(negotiator.annual_objective) - negotiator.total_sales > 0 
                                  ? 'text-red-600' 
                                  : 'text-green-600'
                              }`}>
                                {formatCurrency(Math.abs(calculateObjective(negotiator.annual_objective) - negotiator.total_sales))}
                              </span>
                              {calculateObjective(negotiator.annual_objective) - negotiator.total_sales > 0 ? (
                                <span className="text-xs text-red-500">↑</span>
                              ) : (
                                <span className="text-xs text-green-500">↓</span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap">
                            <span className={`text-sm font-medium ${getCompletionColor(
                              (negotiator.total_sales / calculateObjective(negotiator.annual_objective)) * 100
                            )}`}>
                              {((negotiator.total_sales / calculateObjective(negotiator.annual_objective)) * 100).toFixed(1)}%
                            </span>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {negotiator.sales_count}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}